// src/components/billing/ClubUsageDetails.js
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { ConfirmationModal } from "../ui/ConfirmationModal";
import { apiService } from "../../services/api";
import toast from "react-hot-toast";
import {
  UserGroupIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

const ClubUsageDetails = ({ club, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [usageHistory, setUsageHistory] = useState([]);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [staffActivity, setStaffActivity] = useState([]);
  const [activeTab, setActiveTab] = useState("usage");
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);
  const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] =
    useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const loadClubDetails = useCallback(async () => {
    try {
      const clubId = club.id;
      const [usage, invoices, staff] = await Promise.all([
        apiService.getClubUsageHistory(clubId),
        apiService.getClubInvoices(clubId),
        apiService.getClubStaffActivity(clubId),
      ]);

      setUsageHistory(usage);
      setInvoiceHistory(invoices);
      setStaffActivity(staff);
    } catch (error) {
      console.error("Error loading club details:", error);
      toast.error("Failed to load club details");
    }
    setLoading(false);
  }, [club.id]);

  useEffect(() => {
    loadClubDetails();
  }, [loadClubDetails]);

  const handleRecalculateUsage = async () => {
    setRecalculating(true);
    try {
      const clubId = club.id || club.club_id;
      await apiService.recalculateClubUsage(clubId);
      toast.success("Usage recalculated successfully!");
      setShowRecalculateModal(false);
      await loadClubDetails();
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to recalculate usage");
    }
    setRecalculating(false);
  };

  const handleRecalculateUsageClick = () => {
    setShowRecalculateModal(true);
  };

  const handleGenerateInvoice = async () => {
    setGeneratingInvoice(true);
    try {
      const clubId = club.id || club.club_id;
      await apiService.generateClubInvoice(clubId);
      toast.success("Invoice generated successfully!");
      setShowGenerateInvoiceModal(false);
      await loadClubDetails();
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to generate invoice");
    }
    setGeneratingInvoice(false);
  };

  const handleGenerateInvoiceClick = () => {
    setShowGenerateInvoiceModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  const UsageTab = () => (
    <div className="space-y-6">
      {/* Current Usage Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Current Month Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <UserGroupIcon className="h-6 w-6 text-slate-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">
                {club.active_players || club.billable_players || 0}
              </div>
              <div className="text-sm text-gray-500">Active Players</div>
            </div>
            <div className="text-center">
              <ChartBarIcon className="h-6 w-6 text-green-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">
                {club.total_evaluations || 0}
              </div>
              <div className="text-sm text-gray-500">Evaluations</div>
            </div>
            <div className="text-center">
              <UserGroupIcon className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">
                {club.staff_count || 0}
              </div>
              <div className="text-sm text-gray-500">Staff Members</div>
            </div>
            <div className="text-center">
              <CurrencyDollarIcon className="h-6 w-6 text-orange-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">
                {formatCurrency(club.monthly_charge || 0)}
              </div>
              <div className="text-sm text-gray-500">Monthly Charge</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage History */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Usage History (Last 6 Months)</CardTitle>
            <Button size="sm" onClick={handleRecalculateUsageClick}>
              Recalculate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {usageHistory.map((usage, index) => (
              <div
                key={index}
                className="flex justify-between items-center p-3 border border-gray-200 rounded-lg"
              >
                <div>
                  <div className="font-medium">
                    {new Date(usage.month).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {usage.active_players} players • {usage.evaluations}{" "}
                    evaluations
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">
                    {formatCurrency(usage.charge)}
                  </div>
                  <div className="text-sm text-gray-500">
                    ${usage.rate_per_player}/player
                  </div>
                </div>
              </div>
            ))}
            {usageHistory.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                No usage history available
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Staff Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Staff Activity (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {staffActivity.map((activity, index) => (
              <div
                key={index}
                className="flex justify-between items-center p-3 border border-gray-200 rounded-lg"
              >
                <div>
                  <div className="font-medium">{activity.staff_name}</div>
                  <div className="text-sm text-gray-500">{activity.role}</div>
                </div>
                <div className="text-right">
                  <div className="font-medium">
                    {activity.evaluations_completed} evaluations
                  </div>
                  <div className="text-sm text-gray-500">
                    Last active: {formatDate(activity.last_activity)}
                  </div>
                </div>
              </div>
            ))}
            {staffActivity.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                No staff activity data available
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const InvoicesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Invoice History</h3>
        <Button onClick={handleGenerateInvoiceClick}>
          Generate New Invoice
        </Button>
      </div>

      <div className="space-y-4">
        {invoiceHistory.map((invoice) => (
          <Card key={invoice.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{invoice.invoice_number}</div>
                  <div className="text-sm text-gray-500">
                    {formatDate(invoice.created_at)} • Due:{" "}
                    {formatDate(invoice.due_date)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Period: {formatDate(invoice.period_start)} -{" "}
                    {formatDate(invoice.period_end)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">
                    {formatCurrency(invoice.total_amount)}
                  </div>
                  <Badge
                    variant={
                      invoice.status === "paid"
                        ? "green"
                        : invoice.status === "overdue"
                          ? "red"
                          : "yellow"
                    }
                  >
                    {invoice.status}
                  </Badge>
                </div>
              </div>

              {invoice.line_items && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Players: {invoice.line_items.player_count}</span>
                      <span>
                        {formatCurrency(invoice.line_items.player_charges)}
                      </span>
                    </div>
                    {invoice.line_items.additional_charges > 0 && (
                      <div className="flex justify-between mt-1">
                        <span>Additional charges</span>
                        <span>
                          {formatCurrency(
                            invoice.line_items.additional_charges,
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {invoiceHistory.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No invoices found for this club</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-6 pb-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {club.name || club.club_name}
            </h2>
            <p className="text-gray-600">{club.location}</p>
            <div className="flex items-center space-x-2 mt-2">
              <Badge
                variant={
                  club.subscription_status === "active"
                    ? "green"
                    : club.subscription_status === "past_due"
                      ? "red"
                      : "yellow"
                }
              >
                {club.subscription_status}
              </Badge>
              <Badge variant="blue">{club.plan_name}</Badge>
            </div>
          </div>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("usage")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "usage"
                ? "border-slate-500 text-slate-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Usage Details
          </button>
          <button
            onClick={() => setActiveTab("invoices")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "invoices"
                ? "border-slate-500 text-slate-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Invoices ({invoiceHistory.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "usage" ? <UsageTab /> : <InvoicesTab />}

      {/* Recalculate Usage Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRecalculateModal}
        onClose={() => setShowRecalculateModal(false)}
        onConfirm={handleRecalculateUsage}
        title="Recalculate Usage"
        message={`Recalculate usage for ${
          club.name || club.club_name
        }? This will update all usage metrics and charges.`}
        confirmText="Recalculate"
        loading={recalculating}
      />

      {/* Generate Invoice Confirmation Modal */}
      <ConfirmationModal
        isOpen={showGenerateInvoiceModal}
        onClose={() => setShowGenerateInvoiceModal(false)}
        onConfirm={handleGenerateInvoice}
        title="Generate Invoice"
        message={`Generate a new invoice for ${
          club.name || club.club_name
        }? This will create an invoice based on current usage.`}
        confirmText="Generate Invoice"
        loading={generatingInvoice}
      />
    </div>
  );
};

export default ClubUsageDetails;
